import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api.js';
import { PageHeader, Section } from '@/components/PageHeader.jsx';
import { Button } from '@/components/ui/Button.jsx';
import { Input, FormField } from '@/components/ui/Input.jsx';
import { Select } from '@/components/ui/Select.jsx';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '@/components/ui/Dialog.jsx';
import { Badge } from '@/components/ui/Badge.jsx';
import { DataTable } from '@/components/DataTable.jsx';
import { EmployeePicker } from '@/components/EmployeePicker.jsx';
import { useToast } from '@/components/ui/Toast.jsx';
import { formatLKR, rupeesToCents, centsToRupees } from '@/lib/format.js';
import { Plus, X, Trash2, Eye } from 'lucide-react';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function overrideLabel(inst) {
  if (!inst.override_type || inst.override_type === 'scheduled') return null;
  if (inst.override_type === 'custom') return `Custom: ${formatLKR(inst.override_cents)}`;
  if (inst.override_type === 'full') return 'Full settlement';
  if (inst.override_type === 'skip') return 'Skipped';
  return inst.override_type;
}

function statusVariant(s) {
  if (s === 'paid') return 'success';
  if (s === 'skipped') return 'warning';
  return 'default';
}

function LoanDetailDialog({ loan, onClose }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({ override_type: 'scheduled', override_cents: '' });

  const { data, isLoading } = useQuery({
    queryKey: ['loan_detail', loan.id],
    queryFn: () => api.get(`/loans/${loan.id}`),
    enabled: !!loan.id,
  });

  const saveMutation = useMutation({
    mutationFn: ({ instId, body }) => api.patch(`/loans/${loan.id}/installments/${instId}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['loan_detail', loan.id] });
      qc.invalidateQueries({ queryKey: ['loans', {}] });
      setEditingId(null);
      toast({ variant: 'success', title: 'Settlement override saved' });
    },
    onError: (e) => toast({ variant: 'error', title: 'Failed', description: e.message }),
  });

  function startEdit(inst) {
    setEditDraft({
      override_type: inst.override_type || 'scheduled',
      override_cents: inst.override_cents ? centsToRupees(inst.override_cents) : '',
    });
    setEditingId(inst.id);
  }

  function saveEdit(inst) {
    const body = { override_type: editDraft.override_type };
    if (editDraft.override_type === 'custom') {
      body.override_cents = rupeesToCents(editDraft.override_cents);
    }
    saveMutation.mutate({ instId: inst.id, body });
  }

  const installments = data?.installments ?? [];
  const paidCount = installments.filter((i) => i.status === 'paid').length;
  const settledCents = loan.principal_cents - loan.balance_cents;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            Loan — {data?.full_name ?? loan.full_name}
            <span className="ml-2 text-slate-400 text-sm font-normal">#{loan.emp_no}</span>
          </DialogTitle>
        </DialogHeader>

        {/* Summary row */}
        <div className="grid grid-cols-4 gap-3 text-sm py-1">
          <div>
            <div className="text-slate-500">Principal</div>
            <div className="font-semibold tabular-nums font-mono">{formatLKR(loan.principal_cents)}</div>
          </div>
          <div>
            <div className="text-slate-500">Settled</div>
            <div className="font-semibold tabular-nums font-mono text-success-700">{formatLKR(settledCents)}</div>
          </div>
          <div>
            <div className="text-slate-500">Balance</div>
            <div className="font-semibold tabular-nums font-mono">{formatLKR(loan.balance_cents)}</div>
          </div>
          <div>
            <div className="text-slate-500">Installments</div>
            <div className="font-semibold font-mono">{paidCount} / {loan.installments} paid</div>
          </div>
        </div>

        {/* Installment schedule */}
        <div className="max-h-[420px] overflow-y-auto rounded-md border border-slate-200 dark:border-slate-700">
          {isLoading ? (
            <div className="p-6 text-center text-slate-400 text-sm">Loading…</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-slate-600 dark:text-slate-300 w-10">#</th>
                  <th className="px-3 py-2 text-left font-medium text-slate-600 dark:text-slate-300">Period</th>
                  <th className="px-3 py-2 text-right font-medium text-slate-600 dark:text-slate-300">Scheduled</th>
                  <th className="px-3 py-2 text-right font-medium text-slate-600 dark:text-slate-300">Paid</th>
                  <th className="px-3 py-2 text-left font-medium text-slate-600 dark:text-slate-300">Override</th>
                  <th className="px-3 py-2 text-left font-medium text-slate-600 dark:text-slate-300">Status</th>
                  <th className="px-3 py-2 w-10" />
                </tr>
              </thead>
              <tbody>
                {installments.map((inst) => {
                  const isEditing = editingId === inst.id;
                  const label = overrideLabel(inst);
                  return (
                    <tr key={inst.id} className="border-t border-slate-100 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/50">
                      <td className="px-3 py-2 text-slate-400 font-mono">{inst.seq}</td>
                      <td className="px-3 py-2 font-mono">{MONTHS[inst.month - 1]} {inst.year}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-mono">{formatLKR(inst.total_cents)}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-mono text-slate-500">
                        {inst.paid_cents != null ? formatLKR(inst.paid_cents) : '—'}
                      </td>
                      <td className="px-3 py-2">
                        {isEditing ? (
                          <div className="flex items-center gap-2">
                            <Select
                              className="h-7 text-xs w-36"
                              value={editDraft.override_type}
                              onChange={(e) => setEditDraft({ ...editDraft, override_type: e.target.value, override_cents: '' })}
                            >
                              <option value="scheduled">Scheduled</option>
                              <option value="custom">Custom amount</option>
                              <option value="full">Full settlement</option>
                              <option value="skip">Skip this month</option>
                            </Select>
                            {editDraft.override_type === 'custom' && (
                              <Input
                                className="h-7 text-xs w-28"
                                type="number"
                                step="0.01"
                                placeholder="Amount"
                                value={editDraft.override_cents}
                                onChange={(e) => setEditDraft({ ...editDraft, override_cents: e.target.value })}
                              />
                            )}
                          </div>
                        ) : (
                          label ? (
                            <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${inst.override_type === 'skip' ? 'bg-amber-100 text-amber-700' : inst.override_type === 'full' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                              {label}
                            </span>
                          ) : <span className="text-slate-400 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <Badge variant={statusVariant(inst.status)}>{inst.status}</Badge>
                      </td>
                      <td className="px-3 py-2">
                        {inst.status === 'pending' && (
                          isEditing ? (
                            <div className="flex gap-1">
                              <Button
                                size="xs"
                                variant="default"
                                onClick={() => saveEdit(inst)}
                                disabled={saveMutation.isPending || (editDraft.override_type === 'custom' && !editDraft.override_cents)}
                              >
                                Save
                              </Button>
                              <Button size="xs" variant="ghost" onClick={() => setEditingId(null)}>✕</Button>
                            </div>
                          ) : (
                            <Button size="xs" variant="ghost" onClick={() => startEdit(inst)}>Edit</Button>
                          )
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <DialogFooter>
          <DialogClose asChild><Button variant="outline">Close</Button></DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function LoansPage() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [detailLoan, setDetailLoan] = useState(null);
  const now = new Date();
  const [draft, setDraft] = useState({
    employee_id: '', principal: 0, interest_rate: 0, interest_method: 'none',
    installments: 6, start_year: now.getFullYear(), start_month: now.getMonth() + 1, reference: '',
  });

  const { data = [], isLoading } = useQuery({ queryKey: ['loans', {}], queryFn: () => api.get('/loans') });

  const create = useMutation({
    mutationFn: () => api.post('/loans', {
      employee_id: Number(draft.employee_id),
      principal_cents: rupeesToCents(draft.principal),
      interest_rate: Number(draft.interest_rate || 0),
      interest_method: draft.interest_method,
      installments: Number(draft.installments),
      start_year: Number(draft.start_year),
      start_month: Number(draft.start_month),
      reference: draft.reference || null,
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['loans', {}] }); setOpen(false); toast({ variant: 'success', title: 'Loan created' }); },
    onError: (e) => toast({ variant: 'error', title: 'Failed', description: e.message }),
  });
  const close = useMutation({ mutationFn: (id) => api.post(`/loans/${id}/close`), onSuccess: () => qc.invalidateQueries({ queryKey: ['loans', {}] }) });
  const del = useMutation({ mutationFn: (id) => api.del(`/loans/${id}`), onSuccess: () => qc.invalidateQueries({ queryKey: ['loans', {}] }) });

  const columns = [
    { key: 'emp_no', header: 'Emp No', className: 'font-mono', sortable: true },
    { key: 'full_name', header: 'Name', sortable: true },
    { key: 'principal_cents', header: 'Principal', accessor: (r) => r.principal_cents, render: (r) => formatLKR(r.principal_cents), className: 'text-right tabular-nums font-mono', headClassName: 'text-right' },
    { key: 'settled', header: 'Settled', accessor: (r) => r.principal_cents - r.balance_cents, render: (r) => formatLKR(r.principal_cents - r.balance_cents), className: 'text-right tabular-nums font-mono text-success-700', headClassName: 'text-right' },
    { key: 'balance_cents', header: 'Balance', accessor: (r) => r.balance_cents, render: (r) => formatLKR(r.balance_cents), className: 'text-right tabular-nums font-mono font-semibold', headClassName: 'text-right' },
    { key: 'installments', header: 'Inst.', className: 'text-right font-mono', headClassName: 'text-right' },
    { key: 'interest_method', header: 'Interest', render: (r) => `${r.interest_method}${r.interest_rate ? ` ${r.interest_rate}%` : ''}`, className: 'font-mono' },
    { key: 'reference', header: 'Reference', className: 'font-mono' },
    { key: 'status', header: 'Status', render: (r) => <Badge variant={r.status === 'active' ? 'success' : 'default'}>{r.status}</Badge> },
    { key: '__a', header: '', className: 'w-28 text-right',
      render: (r) => (
        <div className="flex justify-end gap-1">
          <Button variant="ghost" size="icon" title="View installments" onClick={() => setDetailLoan(r)}><Eye className="h-4 w-4 text-slate-500" /></Button>
          {r.status === 'active' && <Button variant="ghost" size="icon" title="Close loan" onClick={() => close.mutate(r.id)}><X className="h-4 w-4 text-amber-600" /></Button>}
          <Button variant="ghost" size="icon" title="Delete loan" onClick={() => del.mutate(r.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
        </div>
      ) },
  ];

  return (
    <div className="space-y-5">
      <PageHeader title="Loans" description="Employee loans with installment recovery"
        actions={<Button onClick={() => setOpen(true)}><Plus className="h-4 w-4" />New Loan</Button>}
      />
      <Section><DataTable columns={columns} data={data} loading={isLoading} exportFilename="loans.csv" /></Section>

      {/* New loan dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Issue Loan</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <FormField label="Employee" required><EmployeePicker value={draft.employee_id} onChange={(v) => setDraft({ ...draft, employee_id: v })} /></FormField>
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Principal (LKR)" required><Input type="number" step="0.01" value={draft.principal} onChange={(e) => setDraft({ ...draft, principal: e.target.value })} /></FormField>
              <FormField label="Installments" required><Input type="number" value={draft.installments} onChange={(e) => setDraft({ ...draft, installments: e.target.value })} /></FormField>
              <FormField label="Interest Method">
                <Select value={draft.interest_method} onChange={(e) => setDraft({ ...draft, interest_method: e.target.value })}>
                  <option value="none">None (interest-free)</option>
                  <option value="flat">Flat Rate</option>
                </Select>
              </FormField>
              <FormField label="Interest Rate (%)"><Input type="number" step="0.01" value={draft.interest_rate} onChange={(e) => setDraft({ ...draft, interest_rate: e.target.value })} /></FormField>
              <FormField label="Start Year"><Input type="number" value={draft.start_year} onChange={(e) => setDraft({ ...draft, start_year: e.target.value })} /></FormField>
              <FormField label="Start Month"><Input type="number" min="1" max="12" value={draft.start_month} onChange={(e) => setDraft({ ...draft, start_month: e.target.value })} /></FormField>
            </div>
            <FormField label="Reference"><Input value={draft.reference} onChange={(e) => setDraft({ ...draft, reference: e.target.value })} /></FormField>
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
            <Button onClick={() => create.mutate()} disabled={!draft.employee_id || create.isPending}>Issue</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Loan detail / installment override dialog */}
      {detailLoan && <LoanDetailDialog loan={detailLoan} onClose={() => setDetailLoan(null)} />}
    </div>
  );
}
